import { CopyIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { notify } from '@/lib/notify.js';

/**
 * "Copy request to admin": copies a request a member without rights can send to an owner or editor.
 * Where the clipboard is not available (an embedded Teams tab, a locked-down browser) the request
 * appears as read-only, pre-selected text, so there is always something to copy by hand.
 */
export function CopyRequestButton({
  text,
  className,
}: {
  text: string;
  className?: string;
}): JSX.Element {
  const [copyFailed, setCopyFailed] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      notify.success('Request copied. Send it to a workspace owner or editor.');
    } catch {
      setCopyFailed(true);
      notify.error('Could not copy. Select the request below and copy it.');
    }
  }

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className ?? 'gap-1 text-xs'}
        onClick={() => void copy()}
      >
        <CopyIcon className="size-3" aria-hidden />
        Copy request to admin
      </Button>
      {copyFailed ? (
        <textarea
          readOnly
          aria-label="Request to send to an admin"
          value={text}
          rows={3}
          autoFocus
          onFocus={(event) => event.currentTarget.select()}
          className="w-full resize-none rounded-md border border-border bg-muted/30 p-2 text-xs text-foreground"
        />
      ) : null}
    </div>
  );
}
